import { Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useCurrentUser } from '@/api/queries/auth';
import { AppShell } from '@/components/layout/AppShell';
import { LoadingState } from '@pixlide/ui';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Coquille des routes authentifiées.
 *
 * Attend la fin de la restauration de session avant de rendre l'application : l'access token
 * vivant en mémoire, il est toujours absent au premier rendu après un rechargement, le temps
 * que l'appel à `/auth/refresh` aboutisse. Sans cette attente, l'utilisateur verrait
 * brièvement l'écran de connexion à chaque F5.
 */
export function AuthenticatedLayout() {
  const isBootstrapped = useAuthStore((state) => state.isBootstrapped);
  const accessToken = useAuthStore((state) => state.accessToken);
  const href = useRouterState({ select: (state) => state.location.href });

  // `bootstrapSession` ne restaure que le jeton, jamais le profil : sans cet appel, monté
  // une fois pour toutes les routes authentifiées, `user` resterait `null` en permanence
  // et le prénom n'apparaîtrait jamais dans la salutation du tableau de bord.
  useCurrentUser();

  if (!isBootstrapped) {
    return <LoadingState label="Restauration de votre session…" />;
  }

  // Garde RÉACTIVE, complément indispensable de `requireAuth` : le `beforeLoad` du routeur
  // ne s'exécute qu'à la navigation. Il laisse donc passer le premier chargement (lancé
  // avant la fin de la restauration de session), et ne voit rien d'une session perdue en
  // cours de route — déconnexion, refresh refusé, 401 définitif, autre onglet. Ici, toute
  // remise à zéro du jeton renvoie immédiatement vers la connexion.
  if (accessToken === null) {
    return <Navigate to="/connexion" search={{ redirect: href }} replace />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
